import { createFileRoute, Link } from "@tanstack/react-router";
import { useLang } from "@/hooks/use-language";
import { LegalShell } from "@/components/legal/LegalShell";

export const Route = createFileRoute("/refunds")({
  component: RefundsPage,
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/refunds" },
      { title: "Refund Policy — Jozori | سياسة الاسترداد — جذوري" },
      {
        name: "description",
        content:
          "Jozori's 30-day money-back guarantee and refund process. ضمان استرداد المبلغ خلال 30 يومًا.",
      },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/refunds" }],
  }),
});

function RefundsPage() {
  const { lang } = useLang();
  const isAr = lang === "ar";

  return (
    <LegalShell
      titleEn="Refund Policy"
      titleAr="سياسة الاسترداد"
      updatedEn="May 19, 2026"
      updatedAr="19 مايو 2026"
    >
      {isAr ? <Ar /> : <En />}
    </LegalShell>
  );
}

function En() {
  return (
    <>
      <h2>30-day money-back guarantee</h2>
      <p>
        We want every family to love Jozori. If you're not satisfied with your
        subscription, you can request a full refund within <strong>30 days</strong>{" "}
        of your initial purchase or renewal date — no questions asked.
      </p>

      <h2>How to request a refund</h2>
      <p>
        Refunds are processed by our Merchant of Record,{" "}
        <strong>Paddle.com</strong>. To request one:
      </p>
      <ol>
        <li>
          Visit{" "}
          <a href="https://paddle.net" target="_blank" rel="noreferrer">paddle.net</a>{" "}
          and look up your order using the email you used at checkout.
        </li>
        <li>
          Or email our support team at{" "}
          <a href="mailto:support@jozori.com">support@jozori.com</a> and we'll
          help you through the process.
        </li>
      </ol>
      <p>
        Refunds typically arrive within 5–10 business days, depending on your
        bank. Once a refund is issued, the associated subscription is cancelled
        and access ends.
      </p>

      <h2>Cancellations</h2>
      <p>
        You can cancel your subscription at any time from{" "}
        <em>Settings → Plan</em>. Cancelling stops future renewals — your
        family keeps access until the end of the current billing period.
      </p>

      <h2>Questions</h2>
      <p>
        Reach us any time at{" "}
        <a href="mailto:support@jozori.com">support@jozori.com</a>. See also our{" "}
        <Link to="/terms">Terms of Service</Link>.
      </p>
    </>
  );
}

function Ar() {
  return (
    <>
      <h2>ضمان استرداد المبلغ خلال 30 يومًا</h2>
      <p>
        نريد أن تحبّ كل عائلة تجربتها مع جذوري. إذا لم تكن راضيًا عن اشتراكك،
        يمكنك طلب استرداد كامل للمبلغ خلال <strong>30 يومًا</strong> من تاريخ
        الشراء الأولي أو التجديد — دون الحاجة إلى أي تبرير.
      </p>

      <h2>كيفية طلب الاسترداد</h2>
      <p>
        تتم معالجة عمليات الاسترداد عبر تاجر التسجيل الخاص بنا{" "}
        <strong>Paddle.com</strong>. للطلب:
      </p>
      <ol>
        <li>
          ادخل إلى{" "}
          <a href="https://paddle.net" target="_blank" rel="noreferrer">paddle.net</a>{" "}
          وابحث عن طلبك باستخدام البريد الإلكتروني الذي استخدمته عند الشراء.
        </li>
        <li>
          أو راسل فريق الدعم على{" "}
          <a href="mailto:support@jozori.com">support@jozori.com</a> وسنرشدك
          خطوة بخطوة.
        </li>
      </ol>
      <p>
        تصل المبالغ المستردّة عادةً خلال 5 إلى 10 أيام عمل بحسب البنك. بمجرد
        إصدار الاسترداد، يُلغى الاشتراك المرتبط به وينتهي الوصول إلى المحتوى.
      </p>

      <h2>الإلغاء</h2>
      <p>
        يمكنك إلغاء اشتراكك في أي وقت من{" "}
        <em>الإعدادات ← الاشتراك</em>. الإلغاء يوقف التجديدات المستقبلية، ويبقى
        وصول عائلتك متاحًا حتى نهاية فترة الفوترة الحالية.
      </p>

      <h2>أسئلة؟</h2>
      <p>
        تواصل معنا في أي وقت على{" "}
        <a href="mailto:support@jozori.com">support@jozori.com</a>. راجع أيضًا{" "}
        <Link to="/terms">الشروط والأحكام</Link>.
      </p>
    </>
  );
}
